import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { scrollToId } from "@/lib/lenis";
import { Laptop, ArrowDownRight } from "lucide-react";
import { Fern, Sprig } from "@/components/site/BrandMark";
import { waChat, waMsg } from "@/components/site/WhatsAppFloat";
import WhatsAppIcon from "@/components/site/WhatsAppIcon";

const lineVariant = {
  hidden: { y: "115%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1, delay: 0.35 + i * 0.14, ease: [0.22, 1, 0.36, 1] },
  }),
};

const DEFAULT_KICKER = "Psicologia · Acolhimento · Transformação";
const DEFAULT_TITLE = "Um espaço\nseguro para";
const DEFAULT_SCRIPT = "você florescer";
const DEFAULT_SUBTITLE = "A Terapia Cognitivo-Comportamental (TCC) ajuda você a compreender seus pensamentos, emoções e comportamentos, desenvolvendo novas formas de lidar com os desafios da vida, com acolhimento, ética e respeito à sua história.";
const DEFAULT_TAGLINE = "Priorize seu bem-estar. Cuide de você.";

export default function Hero({ content }) {
  const waUrl = waChat(content?.whatsapp, waMsg(content));
  const kicker = content?.hero_kicker || DEFAULT_KICKER;
  const titleLines = (content?.hero_title || DEFAULT_TITLE).split("\n").filter(Boolean);
  const script = content?.hero_script || DEFAULT_SCRIPT;
  const subtitle = content?.hero_subtitle || DEFAULT_SUBTITLE;
  const tagline = content?.hero_tagline || DEFAULT_TAGLINE;
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const fernY = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const fernRY = useTransform(scrollYProgress, [0, 1], [0, -120]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, 120]);

  const scrollTo = (id) => scrollToId(id);

  return (
    <section
      id="inicio"
      ref={ref}
      className="relative min-h-[100svh] flex flex-col justify-center overflow-hidden bg-[#FDFBF7] pt-24 pb-16"
      data-testid="hero-section"
    >
      {/* botanical parallax decorations */}
      <motion.div style={{ y: fernY }} className="pointer-events-none absolute -left-16 -top-6 w-[240px] sm:w-[340px]">
        <Fern color="#5B6E5B" opacity={0.16} />
      </motion.div>
      <motion.div style={{ y: fernRY }} className="pointer-events-none absolute -right-24 top-10 w-[280px] sm:w-[420px] scale-x-[-1]">
        <Fern color="#8A9A82" opacity={0.13} />
      </motion.div>

      <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 1 }}
          className="font-body text-[11px] sm:text-xs tracking-[0.4em] uppercase text-[#7A8A7A] mb-8"
        >
          {kicker}
        </motion.p>

        <motion.div style={{ y: textY }} className="max-w-4xl">
          <h1 className="font-display text-[#2C332A] leading-[1.02] text-balance">
            {titleLines.map((line, i) => (
              <span className="line-mask" key={i}>
                <motion.span custom={i} variants={lineVariant} initial="hidden" animate="show" className="block text-5xl sm:text-6xl lg:text-7xl">
                  {line}
                </motion.span>
              </span>
            ))}
            <span className="line-mask leading-[1.2] pb-3">
              <motion.span custom={titleLines.length} variants={lineVariant} initial="hidden" animate="show" className="block">
                <span className="font-script text-[#5B6E5B] text-6xl sm:text-7xl lg:text-8xl leading-[1.1] pr-4">
                  {script}
                </span>
              </motion.span>
            </span>
          </h1>

          <motion.div
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ delay: 1.1, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="origin-left mt-4 h-[3px] w-40 sm:w-56 rounded-full bg-[#C87A5E]"
          />

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.15, duration: 0.9 }}
            className="mt-6 font-body text-base sm:text-lg text-[#4A5D4E] max-w-xl leading-relaxed"
          >
            {subtitle}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.35, duration: 0.9 }}
            className="mt-8 flex flex-wrap items-center gap-4"
          >
            <button
              onClick={() => scrollTo("contato")}
              data-testid="hero-cta-agendar"
              className="group inline-flex items-center gap-3 rounded-full bg-[#5B6E5B] pl-7 pr-3 py-3 text-[#FDFBF7] transition-[transform,background-color] duration-300 hover:bg-[#4A5D4E] hover:scale-[1.03]"
            >
              <span className="font-body text-sm sm:text-base">Agendar atendimento</span>
              <span className="grid place-items-center w-9 h-9 rounded-full bg-[#FDFBF7]/15 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:translate-y-0.5">
                <ArrowDownRight className="w-4 h-4" strokeWidth={1.6} />
              </span>
            </button>

            {waUrl && (
              <a
                href={waUrl}
                target="_blank"
                rel="noreferrer"
                data-testid="hero-cta-whatsapp"
                className="group inline-flex items-center gap-3 rounded-full border border-[#25D366]/45 bg-white/60 pl-7 pr-3 py-3 text-[#1E8A4C] transition-[transform,background-color,border-color] duration-300 hover:bg-[#25D366]/10 hover:border-[#25D366] hover:scale-[1.03]"
              >
                <span className="font-body text-sm sm:text-base">Falar no WhatsApp</span>
                <span className="grid place-items-center w-9 h-9 rounded-full bg-[#25D366]/15 transition-transform duration-300 group-hover:translate-x-0.5">
                  <WhatsAppIcon className="w-4 h-4" />
                </span>
              </a>
            )}
            <div className="inline-flex items-center gap-2.5 rounded-full border border-[#5B6E5B]/20 bg-white/50 px-5 py-3 text-[#4A5D4E]">
              <Laptop className="w-5 h-5 text-[#5B6E5B]" strokeWidth={1.4} />
              <span className="font-body text-sm tracking-wide">Atendimento 100% online</span>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.6, duration: 1 }}
            className="mt-12 flex items-center gap-4"
          >
            <span className="font-script text-3xl sm:text-4xl text-[#C87A5E]">{tagline}</span>
            <Sprig className="hidden sm:block w-24 h-8" color="#5B6E5B" />
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
